import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";

/**
 * Sends login credentials to the API and handles success/error responses.
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {Function} setLoading - Loading state setter
 * @param {Function} navigate - Router navigate function
 * @returns {void}
 */
function performLogin(email, password, setLoading, navigate) {
    if (!email?.length || !password?.length) {
        toast.error("Email ou mot de passe incorrect");
        return;
    }

    setLoading(true);
    axios.post("users/login", { email, password })
        .then(async response => {
            await localStorage.setItem('creativeAdminToken', JSON.stringify(response.data['token']));
            axios.defaults.headers.common['Isl-Token'] = response.data['token'];
            await setLoading(false);
            toast.success("Connexion réussie !");
            navigate('/admin/dashboard');
        })
        .catch(() => {
            setLoading(false);
            toast.error("Email ou mot de passe incorrect");
        });
}

/**
 * Custom hook managing login authentication state and keyboard events.
 * @returns {Object} Login state and handlers: { email, setEmail, password, setPassword, showPassword, setShowPassword, loading, auth }
 */
function useLoginAuth() {
    const navigate = useNavigate();
    const isMounted = useRef(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const auth = useCallback(() => {
        performLogin(email, password, setLoading, navigate);
    }, [email, password, navigate]);

    const handleUserKeyPress = useCallback(event => {
        const { key, keyCode } = event;
        if (email?.length && password?.length && keyCode === 13 && key === 'Enter') {
            performLogin(email, password, setLoading, navigate);
        }
    }, [email, password, navigate]);

    useEffect(() => {
        window.addEventListener("keydown", handleUserKeyPress);
        return () => {
            isMounted.current = true;
            window.removeEventListener("keydown", handleUserKeyPress);
        };
    }, [handleUserKeyPress]);

    return { email, setEmail, password, setPassword, showPassword, setShowPassword, loading, auth };
}

export default useLoginAuth;
